<script lang="ts" setup>
import { camelCase, snakeCase } from 'scule'
import LineStar01 from '@docue/docue-ui-v2/icons/LineStar01.vue'
import SolidStar01 from '@docue/docue-ui-v2/icons/SolidStar01.vue'

import type { AnyTemplate } from '~/types'
import type { TemplateData } from '~/composables/api/template/useFetchTemplate'
import type { UserTemplateData } from '~/composables/api/template/user-template/useFetchUserTemplate'

const props = withDefaults(defineProps<{
  template: AnyTemplate
  iconOnly?: boolean
}>(), {
  iconOnly: false,
})

const { t } = useI18n()
const { danger } = useToast()
const { activeWorkspaceId } = useAuth()

const { mutateAsync: addFavourite, isPending: isPendingAdd } = useMutationFavourite.add()
const { mutateAsync: removeFavourite, isPending: isPendingRemove } = useMutationFavourite.remove()

const isPending = computed(() => isPendingAdd.value || isPendingRemove.value)
const isPendingDelayed = refDelayed(isPending, false)

const { data: allData, isFetching: isFetchingAllData } = useQueryFavourite.all()

const createTemplateTypeCaseVariants = (templateType?: (TemplateData | UserTemplateData)['type']): string[] =>
  templateType ? [camelCase(templateType), snakeCase(templateType)] : []

const favouriteData = computed(() =>
  allData.value?.data?.find(({ relationships }) =>
    createTemplateTypeCaseVariants(relationships?.template?.data.type).includes(getTemplateType(props.template))
    && relationships?.template?.data.id === props.template.id))

const toggleFavourite = async () => {
  if (!activeWorkspaceId.value)
    return

  try {
    if (!favouriteData.value)
      await addFavourite({ template: props.template, workspaceId: activeWorkspaceId.value })
    else await removeFavourite({ id: favouriteData.value.id, workspaceId: activeWorkspaceId.value })
  }
  catch {
    danger(t('unknown-error'))
  }
}

const buttonClasses = computed(() => props.iconOnly
  ? [
      '!px-1',
      '!ring-0',
      '!bg-transparent',
      'hover:!text-yellow',
    ]
  : ['px-3'])
</script>

<template>
  <DTButton
    size="xs"
    class="group/favourite-button"
    :class="buttonClasses"
    :disabled="isPending || isFetchingAllData"
    @click="toggleFavourite"
  >
    <DTSpinner
      v-if="isPendingDelayed || isFetchingAllData"
      :weight="2"
      :size="4"
    />
    <LineStar01
      v-else-if="!favouriteData"
      class="size-4 text-gray-400 group-hover/favourite-button:text-gray-600"
    />
    <SolidStar01
      v-else
      class="size-4 text-yellow-400 group-hover/favourite-button:text-yellow-600"
    />
    <template v-if="!iconOnly">
      {{ t('template.favourite-btn.text') }}
    </template>
  </DTButton>
</template>
